import { zodResolver } from "@hookform/resolvers/zod";
import { useToastController } from "@tamagui/toast";
import { useRef } from "react";
import { Controller, useForm } from "react-hook-form";
import { Platform, TextInput } from "react-native";
import { Masks } from "react-native-mask-input";
import { z } from "zod";

import { Button, Logo, MaskedInput, Text, YStack } from "@/components";
import type { FORMS, SIGN_IN_FLOW } from "./SignIn";
import {
  PHONE_LENGTH,
  USA_COUNTRY_CODE,
  getSmsOptInText,
  usePhoneCodeAuth,
} from "./usePhoneCodeAuth";

export interface PhoneFormProps {
  phone: string;
  setPhone: (phone: string) => void;
  setForm: (form: FORMS) => void;
  signInFlowRef: React.RefObject<SIGN_IN_FLOW>;
  isEmbedded?: boolean;
}

const phoneSchema = z.object({
  phone: z
    .string()
    .length(PHONE_LENGTH, "Please enter a valid 10-digit phone number"),
});

type PhoneFormData = z.infer<typeof phoneSchema>;

export default function PhoneForm({
  phone,
  setPhone,
  setForm,
  signInFlowRef,
  isEmbedded,
}: PhoneFormProps) {
  const { sendCode } = usePhoneCodeAuth(signInFlowRef);
  const { show } = useToastController();

  const phoneRef = useRef<TextInput>(null);

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<PhoneFormData>({
    resolver: zodResolver(phoneSchema),
    defaultValues: { phone },
  });

  const onContinue = handleSubmit(async ({ phone }) => {
    phoneRef.current?.blur?.();
    setPhone(phone);
    const errorMessage = await sendCode(phone);
    if (errorMessage) {
      show(errorMessage, { duration: 10000 });
      phoneRef.current?.focus();
      return;
    }

    setForm("otp");
  });

  return (
    <>
      {isEmbedded ? null : (
        <YStack marginTop="$700" $lg={{ marginTop: "$600" }}>
          <Logo />
        </YStack>
      )}
      <Text
        size="$xxl"
        textAlign="center"
        semibold
        marginVertical="$350"
        marginTop={isEmbedded ? "$200" : "$350"}
      >
        Login or sign up
      </Text>
      <Text textAlign="center" marginBottom="$500" color="$textSecondary">
        Save your conversations, create memories, upload images and documents.
      </Text>
      <YStack width="100%">
        <Controller
          control={control}
          name="phone"
          render={({ field, fieldState }) => (
            <MaskedInput.Controlled
              ref={phoneRef}
              placeholder="Enter your number"
              label="Phone number"
              prefix={<Text>{USA_COUNTRY_CODE}</Text>}
              mask={Masks.USA_PHONE}
              inputMode="tel"
              autoComplete="tel-national"
              textContentType="telephoneNumber"
              value={field.value}
              onChangeText={(_, unmasked) => {
                field.onChange(unmasked);
              }}
              onBlur={field.onBlur}
              onSubmitEditing={() => onContinue()}
              errorText={fieldState.error?.message}
            />
          )}
        />
      </YStack>
      <Button
        marginTop="$400"
        marginBottom="$300"
        width="100%"
        onPress={onContinue}
        mode="primary"
        loading={isSubmitting}
      >
        Continue
      </Button>
      {Platform.OS === "web" && <div id="clerk-captcha" />}
      <Text size="$xs" color="$textSecondary">
        {getSmsOptInText("clicking 'Continue'")}
      </Text>
    </>
  );
}
